import React, { useMemo } from "react";
import { severityMeta, codeLabel, isFixable, hasFixableIssue } from "./qualityMeta";

/**
 * The deterministic quality-gate result for a pending post.
 *
 * Unlike RefreshRationale (which renders the AI's SELF-REPORTED account of its
 * own work), everything here is machine-verified: word counts, distinct link
 * counts, tag balance. It is therefore stated as fact rather than hedged.
 *
 * The gate never blocks a save — the post is always persisted for review — so
 * without this panel a reviewer had to discover problems by reading. The
 * failures were already being written to Firestore by saveToFirestore(); they
 * simply had no UI.
 *
 * Automatic modifications are disclosed here for the same reason the review
 * email discloses them: a reviewer must know which prose the pipeline revised
 * on its own rather than assuming every word came from the original draft.
 *
 * "Fix now" re-runs the same repair pipeline generation uses. It is offered
 * only when at least one issue is actually fixable, because a button that
 * silently does nothing is worse than no button — and each issue is labelled
 * so the reviewer knows which ones will be left for them.
 */
const QualityReportPanel = ({ post, onFix, fixing = false }) => {
  const report = post?.qualityReport;
  const before = post?.qualityReportBefore;

  const repairedCodes = useMemo(
    () => (Array.isArray(post?.repairedCodes) ? post.repairedCodes : []),
    [post?.repairedCodes]
  );

  const issues = useMemo(
    () => (Array.isArray(report?.issues) ? report.issues : []),
    [report?.issues]
  );

  // Posts generated before the quality gate existed have no report at all.
  // Render nothing rather than an empty "all clear" panel, which would falsely
  // imply the post was checked and passed.
  if (!report) return null;

  const errorCount = report.errorCount || 0;
  const warningCount = report.warningCount || 0;
  const clean = issues.length === 0;
  const canFix = !clean && typeof onFix === "function" && hasFixableIssue(issues);

  const headerTone = errorCount > 0
    ? "bg-red-50 border-red-200 text-red-700"
    : warningCount > 0
      ? "bg-amber-50 border-amber-200 text-amber-700"
      : "bg-emerald-50 border-emerald-200 text-emerald-700";

  const borderTone = errorCount > 0
    ? "border-red-200"
    : warningCount > 0
      ? "border-amber-200"
      : "border-emerald-200";

  return (
    <div className="max-w-[860px] mx-auto px-4 pt-6">
      <div className={`bg-white border ${borderTone} rounded-xl shadow-sm overflow-hidden`}>
        <div className={`px-5 py-3 border-b flex items-center gap-2 flex-wrap ${headerTone}`}>
          <span className="font-semibold text-sm">
            {clean ? "✅ Quality checks passed" : "🔍 Quality check results"}
          </span>
          {!clean && (
            <span className="text-xs">
              {errorCount > 0 && `${errorCount} error${errorCount === 1 ? "" : "s"}`}
              {errorCount > 0 && warningCount > 0 && " · "}
              {warningCount > 0 && `${warningCount} warning${warningCount === 1 ? "" : "s"}`}
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            {canFix && (
              <button
                type="button"
                onClick={onFix}
                disabled={fixing}
                className="px-3 py-1.5 bg-cyan-600 text-white text-xs font-semibold rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fixing ? "Fixing…" : "🔧 Fix now"}
              </button>
            )}
            <span className="text-xs opacity-70">Automated checks</span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-4 flex-wrap text-xs text-gray-600">
            <span>
              <strong className="text-gray-800">{report.wordCount ?? "—"}</strong> words
            </span>
            <span>
              <strong className="text-gray-800">{report.distinctInternalLinkCount ?? 0}</strong>{" "}
              distinct internal link
              {report.distinctInternalLinkCount === 1 ? "" : "s"}
              {typeof report.internalLinkCount === "number" && (
                <span className="text-gray-400"> ({report.internalLinkCount} total)</span>
              )}
            </span>
            <span>{report.hasTable ? "✓ has a table" : "✗ no table"}</span>
          </div>

          {repairedCodes.length > 0 && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3">
              <p className="text-sm font-semibold text-cyan-800 mb-1">
                🔧 An automatic repair pass revised this post
              </p>
              <p className="text-xs text-cyan-700 leading-relaxed">
                Fixed{" "}
                {repairedCodes.map((c) => codeLabel(c)).join(", ")}
                {before && typeof before.errorCount === "number" && (
                  <> — errors went from {before.errorCount} to {errorCount}</>
                )}
                . This content was revised by AI after the original draft, so read the
                affected sections as new writing rather than reviewed writing.
              </p>
            </div>
          )}

          {clean ? (
            <p className="text-sm text-gray-600">
              No issues found. Word count, internal linking, table presence, HTML
              structure and keyword placement all meet the requirements.
            </p>
          ) : (
            <ul className="space-y-2">
              {issues.map((issue, i) => {
                const meta = severityMeta(issue.severity);
                const fixable = isFixable(issue.code);
                return (
                  <li
                    key={`${issue.code}-${i}`}
                    className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.className}`}
                      >
                        {meta.icon} {codeLabel(issue.code)}
                      </span>
                      {/* Set expectations per issue: the fix pass attempts some
                          codes and deliberately never attempts others. */}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          fixable
                            ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                            : "bg-gray-50 text-gray-500 border-gray-200"
                        }`}
                      >
                        {fixable ? "auto-fixable" : "manual fix"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{issue.message}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualityReportPanel;
